import React, { useMemo, useState } from 'react';
import Select from '../../shared/Select';
import { ReactComponent as CombinedLayoutIcon } from '../../assets/icons/combined-layout.svg';
import { ReactComponent as SplitLayoutIcon } from '../../assets/icons/split-layout.svg';
import { DESIGN_MODES } from './Step2Container';
import '../../styles/mockup-generator/DesignModeSelector.scss';
import { components } from 'react-select'
import { useSelectedVariantContext } from './context/SelectedVariantContext';
import {actionCaptureBackground, actionChangeDesignMode, actionChangeTabDesign, updateActiveSide} from './context/SelectedVariantContext/action'
import {mergeAttributesByDesignEdited, genAttributes} from './context/SelectedVariantContext/helper'
import {usePreviewArtworks} from '../../hooks/usePreviewArtworks';
import { Button, Tooltip } from 'reactstrap';
import { useEditorSides } from '../../hooks/useEditorSides';
import classNames from 'classnames';

const CustomOption = (props) => {
    const { label, icon, description } = props.data;

    return (
        <components.Option {...props} className={`design-mode-option ${props.isSelected ? 'selected' : ''}`}>
            <div className='design-mode-option-content'>
                {React.cloneElement(icon, { className: 'design-mode-icon' })}
                <div className='design-mode-option-text'>
                    <p className='design-mode-label'>{label}</p>
                    <span className='design-mode-description'>{description}</span>
                </div>
            </div>
        </components.Option>
    );
};

const MenuList = (props) => {
    return (
        <components.MenuList className="design-mode-menu-list" {...props}>
            {props.children}
        </components.MenuList>
    );
};

const SelectedValue = (props) => {
    const { children } = props
    return (
        <components.SingleValue {...props} className={classNames('SingleValue', props.className)}>
            {children}
        </components.SingleValue>
    )
}

const DesignModeSelector = ({ showDesignModeTooltip }) => {
    const {
        state: { history, designMode, selectedSide, productCreatedType, pickerAttributes, designs, product },
        dispatch,
    } = useSelectedVariantContext()
    const { fetchCapturedSides } = usePreviewArtworks()
    const { sides } = useEditorSides()
    const isMockupTypeV2OrV3 = productCreatedType === 'preview3D' || productCreatedType === ''
    const [beforeSideChangeDesignMode, setBeforeSideChangeDesignMode] = useState(null)
    const [showTooltip, setShowTooltip] = useState(showDesignModeTooltip)

    React.useEffect(() => {
        setShowTooltip(showDesignModeTooltip)
    }, [showDesignModeTooltip])

    const options = useMemo(() => {
        const combinedEnabled = !!(
            (isMockupTypeV2OrV3 && sides?.includes('all-in-one'))
            || ['combineLayout', 'regularHasTemplate'].includes(productCreatedType)
        );

        const splitDisabled = ['combineLayout', 'regularHasTemplate'].includes(productCreatedType);
        return [
            {
                value: DESIGN_MODES.COMBINED,
                label: 'Combined layout',
                description: 'Supports uploading a completed and print-ready file to generate mockups and create different products simultaneously',
                icon: <CombinedLayoutIcon />,
                disabled: !combinedEnabled
            },
            {
                value: DESIGN_MODES.SPLIT,
                label: 'Split layout',
                description: 'Supports uploading multiple design layers and customization of specific placements to generate mockups and create one product at a time',
                icon: <SplitLayoutIcon />,
                disabled: splitDisabled
            }
        ]
    }, [productCreatedType, sides]);

    const optionDisabled = options.find(option => option.disabled)
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggle = () => setTooltipOpen(!tooltipOpen);

    const selectedOption = useMemo(() =>
        options.find(option => option.value === designMode) || options.filter(option => !option.disabled)[0],
        [designMode, options]
    );

    const handleChange = (selectedOption) => {
        if (!isMockupTypeV2OrV3) {
            return
        }
        if (selectedOption && selectedOption.value !== designMode) {
            setBeforeSideChangeDesignMode(selectedSide)
            dispatch(actionChangeDesignMode(selectedOption.value));
            designs.forEach(design => {
                Object.keys(design.side).forEach(sideName => {
                    design.side[sideName].layers = []
                })
            })
            const productAttributes = genAttributes(product)
            pickerAttributes.forEach(pickerAttribute => {
                const productAttribute = productAttributes.find(productAttribute => productAttribute.key === pickerAttribute.key)
                const defaultMaterialColor = productAttribute.mockup.preview_meta.default_material_color
                Object.keys(pickerAttribute.safeZone).forEach(sideName => {
                    pickerAttribute.safeZone[sideName].fill = defaultMaterialColor || '#FFFFFF'
                })
            })
            dispatch(actionCaptureBackground([]))
            if (selectedOption.value === DESIGN_MODES.COMBINED) {
                // const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
                dispatch(updateActiveSide('all-in-one'))
                // fetchCapturedSides(fetchedDesigns, false)
                return
            }
            const lastSide = history.logs.findLast(e => e.selectedSide !== 'all-in-one')?.selectedSide
            dispatch(updateActiveSide(beforeSideChangeDesignMode || lastSide))
            return
        }
    };

    return (
        <>
            <div
                id="design-mode-selector-wrapper"
                className={`design-mode-selector me-3 ${['type2D', 'regularNoTemplate'].includes(productCreatedType) ? 'hidden' : ''}`}
            >
                <Select
                    onMenuOpen={() => setShowTooltip(false)}
                    components={{ Option: CustomOption, SingleValue: SelectedValue, MenuList }}
                    className="design-mode-dropdown"
                    value={selectedOption}
                    onChange={handleChange}
                    options={options}
                    isDisabled={!!optionDisabled}
                    isSearchable={false}
                />
            </div>
            {optionDisabled && <Tooltip
                placement="bottom"
                isOpen={tooltipOpen}
                target="design-mode-selector-wrapper"
                toggle={toggle}
                fade={false}
            >
                {`This product is not supported ${optionDisabled.label}`}
            </Tooltip>}
            {!optionDisabled && <Tooltip
                placement="bottom"
                isOpen={showTooltip}
                target="design-mode-selector-wrapper"
                fade={false}
                className="design-mode-highlight"
            >
                <div className="highlight-header">
                    <div className="header-content">
                        <h6 className="fw-500">Your design, your choice ✨</h6>
                    </div>
                </div>

                <div className="highlight-body">
                    <p>
                        Switch between <span className="fw-500">Split layout</span> for flexibility and <span className="fw-500">Combined layout</span> for a streamlined canvas.
                    </p>
                    <Button color={'primary'} onClick={() => setShowTooltip(false)} className={'w-100'}>
                        Got it!
                    </Button>
                </div>
            </Tooltip>}
        </>
    )
}

export default DesignModeSelector
